import { useIsSessionUrgencyEnabled } from '@/features/flags';
import { cn } from '@/lib/cn';
import { sessionUrgencyMessages } from '@heidi/session';
import { Flag } from 'lucide-react';
import { useIntl } from 'react-intl';

interface SessionUrgencyFlagProps {
  isUrgent: boolean | null | undefined;
  /**
   * When the feature is on and this row is not urgent, reserve a fixed-width
   * spacer so avatars stay aligned with urgent rows. Pass `false` when the
   * current list has no urgent sessions so the gutter collapses (no indent).
   * Defaults to `true` for surfaces that always keep alignment.
   */
  reserveGutter?: boolean;
  className?: string;
}

/**
 * Inner glyph — only mounted once we know the session is urgent, so the
 * `useIntl` hook never runs on the (very common) non-urgent row path.
 */
const SessionUrgencyGlyph = ({ className }: { className?: string }) => {
  const intl = useIntl();

  return (
    <Flag
      aria-label={intl.formatMessage(sessionUrgencyMessages.urgent)}
      fill="currentColor"
      className={cn('size-3.5 shrink-0 text-marker-icon-red', className)}
    />
  );
};

/**
 * Compact red flag marker shown at the leading edge of a session row when the
 * session is marked urgent. `isUrgent` comes straight off the session data (see
 * `isSessionUrgent`).
 *
 * The flag lives in a fixed-width leading gutter: on non-urgent rows it can
 * render an equal-footprint spacer so the avatar/name column stays aligned
 * across urgent and non-urgent rows (matching the design — the flag never
 * pushes the avatar right). Call sites that know the current list has no
 * urgent sessions pass `reserveGutter={false}` so the gutter collapses and
 * nothing indents the row. With the feature flag off the whole gutter
 * collapses and nothing renders.
 *
 * Self-gated on the `release-scribe-enable-session-urgency` flag so every
 * display surface (session list, side-nav, consolidated list, Action Center,
 * patient profile) is gated from one place — no call site can forget.
 */
export const SessionUrgencyFlag = ({
  isUrgent,
  reserveGutter = true,
  className,
}: SessionUrgencyFlagProps) => {
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  // Feature entirely off → collapse the gutter, render nothing.
  if (!isSessionUrgencyEnabled) return null;
  // Feature on but row not urgent → optionally reserve the flag's footprint
  // so avatars stay aligned with urgent peers. Skip the spacer when the list
  // has no urgent sessions (`reserveGutter={false}`). This branch (and
  // flag-off above) short-circuits before the glyph's `useIntl`, so the
  // common non-urgent path needs no `IntlProvider`.
  if (!isUrgent) {
    if (!reserveGutter) return null;
    return <span aria-hidden className={cn('size-3.5 shrink-0', className)} />;
  }
  return <SessionUrgencyGlyph className={className} />;
};
